import type {
  AllowedOriginResponseItem,
  ServiceResponse,
  UUID,
  WidgetInstallationCreatePayload,
  WidgetInstallationEmbedCodeResponse,
  WidgetInstallationResponse,
} from '@dopomogai/types';
import { getApiClient } from '../client';
import { handleApiError } from '../utils/handleApiError';

const normalizeUuid = (value?: string | null): string => {
  if (!value) return '';
  return value.replace(/^urn:uuid:/i, '').replace(/^uuid:/i, '').trim();
};

export const widgetInstallationsService = {
  createInstallation: (
    payload: WidgetInstallationCreatePayload
  ): ServiceResponse<WidgetInstallationResponse> => {
    return getApiClient()
      .post<WidgetInstallationResponse>('/widgets/installations', payload)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, 'creating widget installation'));
  },

  listInstallations: (params?: {
    template_id?: UUID | string;
    agent_id?: UUID | string;
  }): ServiceResponse<WidgetInstallationResponse[]> => {
    return getApiClient()
      .get<WidgetInstallationResponse[]>('/widgets/installations', { params })
      .then((res) => res.data)
      .catch((err) => handleApiError(err, 'listing widget installations'));
  },

  getInstallation: (installationId: UUID | string): ServiceResponse<WidgetInstallationResponse> => {
    const cleanId = normalizeUuid(String(installationId));
    return getApiClient()
      .get<WidgetInstallationResponse>(`/widgets/installations/${cleanId}`)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `fetching installation ${cleanId}`));
  },

  patchConfig: (
    installationId: UUID | string,
    configPatch: Record<string, any>
  ): ServiceResponse<WidgetInstallationResponse> => {
    const cleanId = normalizeUuid(String(installationId));
    return getApiClient()
      .patch<WidgetInstallationResponse>(`/widgets/installations/${cleanId}/config`, configPatch)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `patching installation config ${cleanId}`));
  },

  getAllowedOrigins: (
    installationId: UUID | string
  ): ServiceResponse<AllowedOriginResponseItem[]> => {
    const cleanId = normalizeUuid(String(installationId));
    return getApiClient()
      .get<AllowedOriginResponseItem[]>(`/widgets/installations/${cleanId}/allowed-origins`)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `fetching installation origins ${cleanId}`));
  },

  updateAllowedOrigins: (
    installationId: UUID | string,
    originsList: string[]
  ): ServiceResponse<AllowedOriginResponseItem[]> => {
    const cleanId = normalizeUuid(String(installationId));
    return getApiClient()
      .put<AllowedOriginResponseItem[]>(
        `/widgets/installations/${cleanId}/allowed-origins`,
        originsList
      )
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `updating installation origins ${cleanId}`));
  },

  disableInstallation: (
    installationId: UUID | string
  ): ServiceResponse<WidgetInstallationResponse> => {
    const cleanId = normalizeUuid(String(installationId));
    return getApiClient()
      .post<WidgetInstallationResponse>(`/widgets/installations/${cleanId}/disable`)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `disabling installation ${cleanId}`));
  },

  enableInstallation: (
    installationId: UUID | string
  ): ServiceResponse<WidgetInstallationResponse> => {
    const cleanId = normalizeUuid(String(installationId));
    return getApiClient()
      .post<WidgetInstallationResponse>(`/widgets/installations/${cleanId}/enable`)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `enabling installation ${cleanId}`));
  },

  getEmbedCode: (
    installationId: UUID | string
  ): ServiceResponse<WidgetInstallationEmbedCodeResponse> => {
    const cleanId = normalizeUuid(String(installationId));
    return getApiClient()
      .get<WidgetInstallationEmbedCodeResponse>(`/widgets/installations/${cleanId}/embed-code`)
      .then((res) => res.data)
      .catch((err) => handleApiError(err, `fetching installation embed ${cleanId}`));
  },
};

